import React, { useState, useEffect, useCallback } from "react"; // Importe useEffect e useCallback
import { GlobalTable, HeaderLine, Td, Th, Tr } from "../../../../components/styles/table-style";
import { PrioritySelect, Select } from "./style"; // Seus componentes estilizados para selects
import axios from 'axios'; // Importar axios

// --- INTERFACES PARA OS DADOS DO BACKEND ---
// Baseado na estrutura que FashionProductionOrderData deve retornar.
// Ajuste conforme a sua API REALMENTE retorna.

export interface FashionProductionItemsData {
    id: number;
    fashionProductionOrderId: number;
    fashionOrderId: number; // Este é o ID do pedido que você usa para "PED-..."
    quantityForThisProduction: number;
    // Se fashionOrder não vem populado, remova
    fashionOrder?: {
        id: number;
        // Adicione outros campos de FashionOrder se forem relevantes, como 'orderNumber'
    };
}

export interface FashionProductionOrderData {
    id: number; // O ID da OP
    set: string; // Ex: "Camisa A - Cor: Branca", "Conjunto XYZ"
    status: string;
    dressmakerId?: any;
    cutterId?: any;
    color?: any;
    totalItems?: any;
    fashionProduct?: any;
    // Você pode ter campos no backend para prioridade, maquina e dia.
    // Se eles existirem na sua entidade FashionProductionOrder, inclua-os aqui.
    // Exemplo:
    priority?: string; // Corresponde à prioridade
    machine?: string;  // Corresponde à máquina
    productionDay?: string; // Corresponde ao dia

    fashionProductionItems?: FashionProductionItemsData[];
    // totalItems geralmente é calculado no frontend, mas se o backend enviar, use-o
    // totalItems: number; // Se o backend já enviar, descomente
}

// Interface para a estrutura de dados de cada linha na sua tabela PCP
// Esta é a estrutura INTERNA do seu estado `tableData`
interface TableRowData {
    op: string; // Ex: "OP-123"
    pedido: string; // Ex: "PED-456, PED-789"
    qtdItens: number;
    etapa: string;
    prioridade: string; // Valor selecionado da prioridade
    maquina: string;     // Valor selecionado da máquina
    dia: string;         // Valor selecionado do dia
    // Adicione um ID da ordem de produção original para poder fazer PATCHs
    originalOrderId: number;
}

// Opções para os selects (mantidas como no seu código)
const PRIORIDADE_OPTIONS = ['Alta', 'Média', 'Baixa'];
const MAQUINA_OPTIONS = ['Máquina A', 'Máquina B', 'Máquina C', 'Máquina D'];
const DIA_OPTIONS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];

const TablePCP: React.FC = () => {
    // Mantém todas as colunas originais do seu código
    const columns = ['OP', 'Pedido', 'Qtd. de itens', 'Etapa', 'Prioridade', 'Máquina', 'Dia'];

    // `tableData` agora será preenchido pela API
    const [tableData, setTableData] = useState<TableRowData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Função para buscar e processar os dados das ordens de produção
    const fetchAndProcessProductionOrders = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${process.env.REACT_APP_FASHION_ORDERS_BASE_URL}/fashion-production-orders`);
            const rawOrders: FashionProductionOrderData[] = response.data;
            console.log('Dados brutos da API (Ordens de Produção):', rawOrders);

            // Mapear e transformar os dados da API para o formato TableRowData
            const formattedData: TableRowData[] = rawOrders.map(order => {
                // Extrair o número da OP
                const opNumber = `OP-${order.id}`; // Ou use order.orderNumber se a API retornar

                // Extrair os números de pedido (pode ter múltiplos)
                const pedidos = order.fashionProductionItems
                    ?.map(item => `PED-${item.fashionOrderId}`)
                    .join(', '); // Junta todos os pedidos com vírgula

                // Calcular a quantidade total de itens para esta OP
                const totalItems = order.fashionProductionItems?.reduce((sum, item) => sum + item.quantityForThisProduction, 0) || 0;

                // Definir valores iniciais para selects, usando dados da API ou padrões
                const initialPriority = order.priority && PRIORIDADE_OPTIONS.includes(order.priority) ? order.priority : PRIORIDADE_OPTIONS[0];
                const initialMachine = order.machine && MAQUINA_OPTIONS.includes(order.machine) ? order.machine : MAQUINA_OPTIONS[0];
                const initialDay = order.productionDay && DIA_OPTIONS.includes(order.productionDay) ? order.productionDay : DIA_OPTIONS[0];


                return {
                    originalOrderId: order.id, // Guardar o ID original para futuras atualizações na API
                    op: opNumber,
                    pedido: pedidos || 'N/A', // Se não houver itens, exibe N/A
                    qtdItens: totalItems,
                    etapa: order.status || 'N/A', // Usando 'status' da OP como 'etapa', ajuste se for outro campo
                    prioridade: initialPriority,
                    maquina: initialMachine,
                    dia: initialDay,
                };
            });

            setTableData(formattedData);
        } catch (err: any) {
            console.error("Erro ao buscar ordens de produção:", err);
            setError("Não foi possível carregar as ordens de produção.");
        } finally {
            setIsLoading(false);
        }
    }, []); // As dependências estão vazias porque `axios` é global e as opções são constantes.

    // Use useEffect para chamar a função de busca de dados uma vez, quando o componente montar
    useEffect(() => {
        fetchAndProcessProductionOrders();
    }, [fetchAndProcessProductionOrders]); // `fetchAndProcessProductionOrders` é estável devido ao `useCallback`

    // Função para lidar com a mudança nos selects (mantida como estava)
    const handleSelectChange = (
        rowIndex: number,
        field: keyof TableRowData,
        value: string
    ) => {
        setTableData(prevData => {
            const newData = [...prevData];
            const updatedRow = { ...newData[rowIndex], [field]: value };
            newData[rowIndex] = updatedRow;
            return newData;
        });
        // Opcional: Se você quiser enviar a atualização para o backend imediatamente
        // const updatedOrder = tableData[rowIndex];
        // updateOrderInBackend(updatedOrder.originalOrderId, field, value);
    };

    // Função para enviar atualizações para o backend (exemplo, descomente e implemente se precisar)
    /*
    const updateOrderInBackend = async (orderId: number, field: keyof TableRowData, value: string) => {
        try {
            // Adapte o nome do campo para o que sua API espera
            let payload: { [key: string]: any } = {};
            if (field === 'prioridade') payload.priority = value;
            else if (field === 'maquina') payload.machine = value;
            else if (field === 'dia') payload.productionDay = value;
            // ... adicione outros campos se houver

            await axios.patch(`${process.env.REACT_APP_FASHION_ORDERS_BASE_URL}/fashion-production-orders/${orderId}`, payload);
            console.log(`OP-${orderId} atualizada com sucesso: ${field} = ${value}`);
            // Exibir uma mensagem de sucesso para o usuário
        } catch (err) {
            console.error(`Erro ao atualizar OP-${orderId} no backend:`, err);
            // Reverter a UI ou exibir uma mensagem de erro
            // fetchAndProcessProductionOrders(); // Recarregar dados para sincronizar
        }
    };
    */

    if (isLoading) {
        return <p>Carregando ordens de produção...</p>;
    }

    if (error) {
        return <p style={{ color: 'red' }}>Erro: {error}</p>;
    }

    if (tableData.length === 0) {
        return <p>Nenhuma ordem de produção encontrada.</p>;
    }

    return (
        <GlobalTable>
            <thead>
                <HeaderLine>
                    {columns.map((item, index) => (
                        <Th key={index}>{item}</Th>
                    ))}
                </HeaderLine>
            </thead>
            <tbody>
                {tableData.map((rowData, rowIndex) => (
                    // Use originalOrderId como key, pois é o identificador único da OP
                    <Tr key={rowData.originalOrderId}>
                        <Td>{rowData.op}</Td>
                        <Td>{rowData.pedido}</Td>
                        <Td>{rowData.qtdItens}</Td>
                        <Td>{rowData.etapa}</Td>

                        {/* Coluna Prioridade como Select */}
                        <Td>
                            <PrioritySelect
                                value={rowData.prioridade}
                                onChange={(e) => handleSelectChange(rowIndex, 'prioridade', e.target.value)}
                            >
                                {PRIORIDADE_OPTIONS.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </PrioritySelect>
                        </Td>

                        {/* Coluna Máquina como Select */}
                        <Td>
                            <Select
                                value={rowData.maquina}
                                onChange={(e) => handleSelectChange(rowIndex, 'maquina', e.target.value)}
                            >
                                {MAQUINA_OPTIONS.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </Select>
                        </Td>

                        {/* Coluna Dia como Select */}
                        <Td>
                            <Select
                                value={rowData.dia}
                                onChange={(e) => handleSelectChange(rowIndex, 'dia', e.target.value)}
                            >
                                {DIA_OPTIONS.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </Select>
                        </Td>
                    </Tr>
                ))}
            </tbody>
        </GlobalTable>
    );
};

export default TablePCP;